import { useEffect, useMemo } from "react";
import { Application, ReviewStatus } from "@/types/application";
import {
  InterpreterEntities,
  useInterpreterStore,
} from "@coltorapps/builder-react";
import { entitiesComponents } from "@/const/form-builder";
import { basicFormBuilder } from "./form/builder/definition";
import { useSetAtom } from "jotai";
import {
  interpreterStoreAtom,
  runtimeApplicationAtom,
  selectedApplicantAtom,
} from "@/store";
import { EntityKey } from "@/types/form-builder";
import { Textarea } from "@ui/textarea";
import { usePreloadMasterDataForExpressions } from "@/hooks/useMasterData";
import { useCodeHelper } from "@/hooks/useCode/useCodeHelper";

type Props = {
  application: Application;
};

export default function ApproverReadonlyForm({ application }: Props) {
  const { formInstance } = application;
  const { form, data } = formInstance;

  const isReadonly =
    application.reviewStatus === ReviewStatus.Approved ||
    application.reviewStatus === ReviewStatus.Rejected;

  const setIStore = useSetAtom(interpreterStoreAtom);
  const setRuntimeApplication = useSetAtom(runtimeApplicationAtom);
  const setSelectedApplicant = useSetAtom(selectedApplicantAtom);

  const schemaSignature = useMemo(
    () => JSON.stringify(form.schema ?? {}),
    [form.schema],
  );
  const stableFormSchema = useMemo(() => form.schema, [schemaSignature]);

  const effectiveFormSchema = useMemo(() => {
    if (!isReadonly) return stableFormSchema;

    const entities = Object.fromEntries(
      Object.entries(stableFormSchema.entities ?? {}).map(
        ([entityId, entity]) => {
          const current = entity as typeof entity & {
            attributes?: Record<string, unknown>;
            type?: string;
          };
          const attributes = current.attributes ?? {};
          const nextGridHeaders =
            current.type === EntityKey.grid &&
            Array.isArray(attributes.gridHeaders)
              ? attributes.gridHeaders.map((header) => ({
                  ...header,
                  readonly: true,
                }))
              : attributes.gridHeaders;

          const nextAttributes: Record<string, unknown> = {
            ...attributes,
            readonly: true,
            // ...("disabled" in attributes ? { disabled: true } : {}),
            ...(nextGridHeaders ? { gridHeaders: nextGridHeaders } : {}),
          };

          return [
            entityId,
            {
              ...current,
              attributes: nextAttributes,
            },
          ];
        },
      ),
    ) as typeof stableFormSchema.entities;

    return {
      ...stableFormSchema,
      entities,
    };
  }, [isReadonly, stableFormSchema]);

  const initialDataSignature = useMemo(
    () => JSON.stringify(data ?? {}),
    [data],
  );
  const stableInitialData = useMemo(() => data ?? {}, [initialDataSignature]);

  // We still want to compile schema-level references (e.g. label/placeholder),
  // but we do NOT want expression field values to re-evaluate. Expression
  // entities are marked with `disableEvaluation` in the schema mapping above.
  const { getCompiledSchema } = useCodeHelper({
    formSchema: effectiveFormSchema,
    formData: stableInitialData,
    application,
  });
  const { cache: expressionMasterDataCache } =
    usePreloadMasterDataForExpressions(effectiveFormSchema);
  const compiledSchema = useMemo(
    () => getCompiledSchema(),
    [getCompiledSchema, expressionMasterDataCache],
  );

  const interpreterStore = useInterpreterStore(
    basicFormBuilder,
    compiledSchema,
    {
      initialData: stableInitialData,
    },
  );

  // Keep the store in sync with incoming form instance data.
  useEffect(() => {
    interpreterStore.setData({
      entitiesValues: data,
      entitiesErrors: {},
    });
  }, [data, initialDataSignature, interpreterStore]);

  // Keep interpreter store available for expression execution.
  useEffect(() => {
    setIStore((prev) => (prev === interpreterStore ? prev : interpreterStore));
  }, [interpreterStore, setIStore]);

  useEffect(() => {
    setRuntimeApplication(application);
  }, [application, setRuntimeApplication]);

  useEffect(() => {
    // Keep expression context aligned with ApplicationForm behavior.
    setSelectedApplicant(application.applicant ?? null);
  }, [application.applicant, setSelectedApplicant]);

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      onKeyDownCapture={(e) => {
        if (e.key !== "Enter") return;
        const target = e.target as HTMLElement | null;
        if (target?.tagName.toLowerCase() === "textarea") return;
        e.preventDefault();
      }}
      className="space-y-6"
    >
      <InterpreterEntities
        interpreterStore={interpreterStore}
        components={entitiesComponents}
      />

      <div className="flex flex-col py-4 gap-3">
        <div className="flex flex-col gap-3">
          <dt className="text-sm font-medium text-dark">Comment</dt>
          <dd className="mt-1 text-base text-dark font-semibold whitespace-pre-wrap flex flex-row gap-3">
            <Textarea
              className="h-29"
              value={application.comment ?? ""}
              disabled
              readOnly
            />
          </dd>
        </div>
      </div>
    </form>
  );
}
